import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ApiButton,
  Button,
  ContentTemplate,
  Grid,
  Icon,
  KeyValue,
  Pill,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { candidateService } from '../../api';
import NavigationButtons from '../../components/NavigationButtons';
import RouteKey from '../../routes/key';
import { ApplicationStatus } from '../../api/candidate/exam/model';
import ReviewWithdrawalDataTable from './ReviewWithdrawalDataTable';
import ReviewWithdrawalCandidateParticulars from './ReviewWithdrawalCandidateParticulars';
import ReviewWithdrawalDetails from './ReviewWithdrawalDetails';
import { stringToLongDateAndShortTime } from '../../utils/DateUtils';
import { FileUploadState } from '../../models/File';
import DownloadProgressModal from '../../components/modals/DownloadProgressModal';

const useStyles = makeStyles()((theme) =>
({
  subheader: {
    marginBottom: theme.spacing(4),
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  personalDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  requestTable: {
    marginBottom: theme.spacing(8),
  },
  buttonsContainer: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(7),
  },
  confirmButtonsContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  downloadPdf: {
    display: 'flex',
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(3),
  },
}));

const { exam, withdrawal } = RouteKey.child;
const { examRegistrations, additionalDocuments } = RouteKey.withdrawal.child;

interface ViewApplicationProps {
  isTransactionHistory?: boolean;
}

const ViewApplication = ({ isTransactionHistory = false }: ViewApplicationProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const { uuid } = useParams<{ uuid: string }>();
  const [shouldOpenPdf, setShouldOpenPdf] = useState<boolean>(false);
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const { data: withdrawalDetails } = candidateService.exam.candidate.useGetWithdrawDetails(uuid);

  const {
    data: downloadSummaryPdfData,
    refetch: fetchSummaryPDF,
    isFetching,
    status: GetWithdrawalDownloadPDFStatus,
  } = candidateService.exam.candidate.useGetWithdrawalDownloadPDF(
    uuid,
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    }
  );

  const handleBack = () => {
    history.push(`/${exam}/${examRegistrations.route}`);
  };

  const handleAddDocument = () => {
    history.push(`/${withdrawal}/${examRegistrations.route}/${uuid}/${additionalDocuments.route}`);
  };

  if (downloadSummaryPdfData?.url && shouldOpenPdf) {
    window.open(downloadSummaryPdfData?.url, '_blank');
    setShouldOpenPdf(false);
  }

  const additionalComponentRenderer = () => {
    if (isFetching) {
      return (
        <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />
      );
    }
    return (
      <ApiButton
        id="link-download"
        // className={classes.actionButton}
        variant="text"
        color="primary"
        status={GetWithdrawalDownloadPDFStatus}
        onClick={() => fetchSummaryPDF()}
        size="small"
      >
        <IconWithLabel
          iconName={IconName.download}
          iconSize="M"
          label={t('withdrawal->>ViewApplication.DownloadSummary')}
          typographyProps={{
            variant: 'h5',
          }}
        />
      </ApiButton>
    );
  };

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  if (!withdrawalDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ContentTemplate isDividerBottom type="large" isContinuable>
        <Grid className={classes.personalDetailsContainer}>
          <Grid container direction="row" alignItems="flex-start">
            <SectionHeader
              title={t('withdrawal->>ViewApplication.ApplicationDetails')}
            >
              {additionalComponentRenderer()}
            </SectionHeader>
          </Grid>
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="applicationId"
                keyText={t('withdrawal->>ViewApplication.ApplicationId')}
                valueText={withdrawalDetails.applicationId}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="status"
                keyText={t('withdrawal->>ViewApplication.Status')}
                valueText={
                  <Pill
                    variant={
                      withdrawalDetails.status === ApplicationStatus.WITHDRAWN ? 'default' : 'info'
                    }
                    label={withdrawalDetails.status}
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="applicationDateTime"
                keyText={t('withdrawal->>ViewApplication.ApplicationDateTime')}
                valueText={withdrawalDetails.applicationDateTime}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12}>
              <KeyValue
                id="lastUpdatedDateTime"
                keyText={t('withdrawal->>ViewApplication.LastUpdatedDateTime')}
                valueText={
                  stringToLongDateAndShortTime(withdrawalDetails.lastUpdatedDateTime) || '-'
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentTemplate>
      <ReviewWithdrawalDetails withdrawalDetails={withdrawalDetails} isSectionHeader={false} />
      <ReviewWithdrawalCandidateParticulars withdrawalDetails={withdrawalDetails} />
      <ContentTemplate continuous type="full">
        <Typography variant="h2" className={classes.subheader}>
          {t('withdrawal->>ReviewWithdrawal.RegisteredSubjects')}
        </Typography>
        <Grid>
          <KeyValue
            id="examYear"
            keyText={t('withdrawal->>ReviewWithdrawal.ExamYear')}
            valueText={withdrawalDetails.examYear}
          />
        </Grid>
        <Grid className={classes.requestTable}>
          <ReviewWithdrawalDataTable withdrawalDetails={withdrawalDetails} />
        </Grid>
        <Grid className={classes.buttonsContainer}>
          {isTransactionHistory ? (
            <Button
              id="button-selectSubject-back"
              className={classes.button}
              variant="outlined"
              onClick={() =>
                history.push(`/${exam}/${examRegistrations.route}`, {
                  from: 'TransactionHistoryPages',
                })
              }
            >
              {t('exam->>RegistrationSummary.BackToTransactionHistory')}
            </Button>
          ) : (
            <NavigationButtons
              backLabel={t('withdrawal->>ViewApplication.Back')}
              procceedLabel={t('withdrawal->>ViewApplication.AddDocument')}
              onBackClick={handleBack}
              onProceedClick={handleAddDocument}
              showProceedButton={withdrawalDetails.status !== 'Withdrawn'}
            />
          )}
        </Grid>
      </ContentTemplate>
      {fileStatus === FileUploadState.FAILED && (
        <DownloadProgressModal
          modalState
          fileDownloadStatus={FileUploadState.FAILED}
          onDialogClose={handleCloseDownloadErrorPrompt}
        />
      )}
    </>
  );
};

export default ViewApplication;
